import { CommonModule, LocationStrategy } from '@angular/common';
import { HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ErrorHandler, Injector, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CoreCommonModule } from '@core/common.module';
import { CoreSidebarModule, CoreThemeCustomizerModule } from '@core/components';
import { CardSnippetModule } from '@core/components/card-snippet/card-snippet.module';
import { CoreTouchspinModule } from '@core/components/core-touchspin/core-touchspin.module';
import { CoreModule } from '@core/core.module';
import { CoreDirectivesModule } from '@core/directives/directives';
import { CorePipesModule } from '@core/pipes/pipes.module';
import { NgbAccordionModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { FlatpickrModule } from 'angularx-flatpickr';
import { coreConfig } from 'app/app-config';
import { AppComponent } from 'app/app.component';
import { ContentHeaderModule } from 'app/layout/components/content-header/content-header.module';
import { LayoutModule } from 'app/layout/layout.module';
import 'hammerjs';
import { ColorPickerModule } from 'ngx-color-picker';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ToastrModule } from 'ngx-toastr'; // For auth after login toast
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { ConfirmInvitationModule } from './auth/confirm-invitation/confirm-invitation.module';
import { AuthGuard } from './auth/helpers';
import { CrmAuthGuard } from './auth/helpers/crm-auth.guard';
import { RedirectGuard } from './auth/helpers/redirect.guard';
import { SessionGuard } from './auth/helpers/session.guard';
import { apiAuthFallbackInterceptor } from './auth/interceptors/api-auth-fallback.service';
import { apiUrlInterceptor } from './auth/interceptors/api-url.interceptor';
import { collectSessionExpirationInterceptor } from './auth/interceptors/collect-session-expiration.interceptor';
import { csrfCookieMishmashInterceptor } from './auth/interceptors/csrf-cookie-mishmash.interceptor';
import { languageHttpInterceptor } from './auth/interceptors/language.interceptor';
import { sessionBasedAuthInterceptor } from './auth/interceptors/session-based-auth.interceptor';
import { sessionStoredTokenInterceptor } from './auth/interceptors/session-stored-token.interceptor';
import { SocialLoginsLinksComponent } from './auth/social-logins/social-logins-links.component';
import { UserStateProvider } from './auth/user-state.provider';
import { CrmLoginComponent } from './crm-login/crm-login.component';
import { MuuriItemsDirective } from './directives/muuri-items.directive';
import { MuuriDirective } from './directives/muuri.directive';
import { DomListenerService } from './global/dom-listener.service';
import { PermissionsModule } from './global/permissions/permissions.module';
import { BreadcrumbModule } from './layout/components/content-header/breadcrumb/breadcrumb.module';
import { NavbarModule } from './layout/components/navbar/navbar.module';
import { OnlineContractModule } from './main/online-contract/onlinecontract.module';
import { AuthLoginV2Component } from './main/pages/authentication/auth-login/auth-login.component';
import { AuthPagesComponent } from './main/pages/authentication/auth-pages/auth-pages.component';
import { AuthRegisterComponent } from './main/pages/authentication/auth-register/auth-register.component';
import { AuthenticationModule } from './main/pages/authentication/authentication.module';
import { ForgotPasswordComponent } from './main/pages/authentication/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './main/pages/authentication/reset-password/reset-password.component';
import { ConfirmationEmailComponent } from './main/pages/authentication/confirmation-email/confirmation-email.component';
import { EmailVerifyComponent } from './main/pages/authentication/email-verify/email-verify.component';
import { DashboardComponent } from './main/pages/dashboard/dashboard.component';
import { DashboardModule } from './main/pages/dashboard/dashboard.module';
import { DocumentsComponent } from './main/pages/documents/documents.component';
import { DocumentsModule } from './main/pages/documents/documents.module';
import { MiscellaneousModule } from './main/pages/miscellaneous/miscellaneous.module';
import { TemplatesModule } from './main/pages/templates/tamplates.module';
import { TemplatesComponent } from './main/pages/templates/templates.component';
import { SharedModule } from './main/shared/shared/shared.module';
import { SigningPagesModule } from './main/signing-pages/signingpages.module';
import { ParameterHashLocationStrategy } from './ParameterHashLocationStrategy';
import { AddonPermissionPipe } from './pipes/addon-permission.pipe';
import { AuthEffects } from './store/effects/auth.effects';
import { CategoriesEffects } from './store/effects/categories.effects';
import { RolesEffects } from './store/effects/roles.effects';
import { reducers } from './store/reducers';
import { TranslocoRootModule } from './transloco-root.module';
import { CodeInputModule } from 'angular-code-input';
import { VerifyOtpComponent } from './main/pages/authentication/verify-otp/verify-otp.component';
import * as Sentry from "@sentry/angular";
import { EmailVerifyGuard } from './main/pages/authentication/email-verify/email-verify.guard';
import { IframeableModule } from './main/pages/iframeable/iframeable-select-template/iframeable.module';
import { SignAbleEformsModule } from './main/pages/signable-eform/signable-eform.module';
import { SignableEformComponent } from './main/pages/signable-eform/signable-eform.component';

import { CustomFeatherIconProvider } from "../@core/services/custom-feather-icon.provider";
import { TeamInvitationComponent } from './main/pages/team-invitation/team-invitation.component';
import { iframeInterceptor } from './auth/interceptors/iframe.interceptor';
import { AuthSsoComponent } from './main/pages/authentication/auth-sso/auth-sso.component';

export let AppInjector: Injector;

const httpInterceptorProviders = [
    provideHttpClient(
        withInterceptors([
            apiUrlInterceptor,
            languageHttpInterceptor,
            sessionBasedAuthInterceptor,
            csrfCookieMishmashInterceptor,
            apiAuthFallbackInterceptor,
            sessionStoredTokenInterceptor,
            collectSessionExpirationInterceptor,
            iframeInterceptor
        ]),
    )
]

export function getProviders() {
    const providers = [
        DomListenerService,
        ...httpInterceptorProviders,
        {
            provide: LocationStrategy,
            useClass: ParameterHashLocationStrategy
        },
        AuthGuard,
        SessionGuard,
        RedirectGuard,
        EmailVerifyGuard,
        CrmAuthGuard,
        AddonPermissionPipe,
        provideNgxMask({ validation: false }),
        UserStateProvider,
        CustomFeatherIconProvider
    ];

    if (environment.sentry?.enabled) {
        providers.push({
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        });
    }

    return providers;
}

@NgModule({
    declarations: [
        AppComponent,
        DashboardComponent,
        TemplatesComponent,
        DocumentsComponent,
        AuthLoginV2Component,
        AuthRegisterComponent,
        AuthPagesComponent,
        ForgotPasswordComponent,
        ResetPasswordComponent,
        ConfirmationEmailComponent,
        EmailVerifyComponent,
        MuuriDirective,
        MuuriItemsDirective,
        CrmLoginComponent,
        VerifyOtpComponent,
        SignableEformComponent,
        TeamInvitationComponent,
        AuthSsoComponent
    ],
    imports: [CommonModule, BrowserModule, AppRoutingModule, FormsModule, ReactiveFormsModule, BrowserAnimationsModule, HttpClientModule, NgbModule, ToastrModule.forRoot({
        positionClass: 'toast-bottom-left'
    }), CoreModule.forRoot(coreConfig), CoreCommonModule, CoreSidebarModule, CoreThemeCustomizerModule, LayoutModule, NavbarModule, NgbAccordionModule, ColorPickerModule, CoreTouchspinModule, SigningPagesModule, OnlineContractModule, MiscellaneousModule, AuthenticationModule, NgSelectModule, ContentHeaderModule, AutocompleteLibModule, BreadcrumbModule, CoreDirectivesModule, CorePipesModule, CardSnippetModule, BreadcrumbModule, SharedModule, NgScrollbarModule, ConfirmInvitationModule, StoreModule.forRoot(reducers), EffectsModule.forRoot([AuthEffects, CategoriesEffects, RolesEffects]), StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: environment.production, // Restrict extension to log-only mode
    }), FlatpickrModule.forRoot(), NgbModule, DashboardModule, DocumentsModule, TemplatesModule, NgxMaskDirective, NgxMaskPipe, PermissionsModule, TranslocoRootModule, CodeInputModule.forRoot({
        codeLength: 6,
    }), SocialLoginsLinksComponent, IframeableModule, SignAbleEformsModule],
    bootstrap: [AppComponent],
    providers: getProviders()
})

export class AppModule {
    constructor(private injector: Injector) {
        AppInjector = this.injector;
    }
}
